import type { GameState, JobId, Modifiers, ResourceId } from './types';
import {
  BALANCE,
  FOOD_RESOURCES,
  JOBS,
  RESOURCES,
  RESOURCE_IDS,
} from './content';
import { jobMultiplier, resourceMultiplier, storageCap } from './modifiers';

export interface ProductionResult {
  /** Gross production per second, after all multipliers. */
  production: Record<ResourceId, number>;
  /** Everything drawn down per second: job inputs plus food eaten. */
  consumption: Record<ResourceId, number>;
  /** Net change per second. */
  net: Record<ResourceId, number>;
  /** Fraction of each job's workers actually able to work. */
  jobEfficiency: Record<JobId, number>;
  /** Storage caps in effect this tick. */
  caps: Record<ResourceId, number>;
  /** Nutrition demanded per second by the whole tribe. */
  nutritionDemand: number;
  /** Nutrition actually supplied per second. */
  nutritionSupplied: number;
  /** Shelter capacity — the population ceiling. */
  shelter: number;
  /** Tribe members with no job assigned. */
  idlePopulation: number;
}

const EMPTY_RATE = 0;

function zeroed(): Record<ResourceId, number> {
  const record: Record<ResourceId, number> = {};
  for (const resourceId of RESOURCE_IDS) record[resourceId] = EMPTY_RATE;
  return record;
}

/**
 * Compute every rate for the current instant. Pure: reads state, writes nothing.
 * `deltaSeconds` is used only to decide whether a stock is large enough to keep
 * an input-hungry job running this tick.
 */
export function computeProduction(
  state: GameState,
  modifiers: Modifiers,
  deltaSeconds: number,
): ProductionResult {
  const step = Math.max(deltaSeconds, 0.05);

  const caps: Record<ResourceId, number> = {};
  for (const resource of RESOURCES) {
    caps[resource.id] = storageCap(state, modifiers, resource.id, resource.baseCap);
  }

  const production = zeroed();
  const consumption = zeroed();
  const jobEfficiency: Record<JobId, number> = {};

  // Pass one: jobs that need no inputs always run at full efficiency. Their
  // output is available to feed the jobs that do need inputs this same tick.
  for (const job of JOBS) {
    const assigned = state.jobs[job.id] ?? 0;
    if (assigned <= 0) {
      jobEfficiency[job.id] = 0;
      continue;
    }
    if (job.inputs) continue;

    jobEfficiency[job.id] = 1;
    const jobBoost = jobMultiplier(modifiers, job.id);
    for (const [resourceId, perWorker] of Object.entries(job.outputs)) {
      const rate =
        (perWorker ?? 0) * assigned * jobBoost * resourceMultiplier(state, modifiers, resourceId);
      production[resourceId] = (production[resourceId] ?? 0) + rate;
    }
  }

  // Pass two: input-hungry jobs throttle to whatever their inputs can sustain.
  for (const job of JOBS) {
    const assigned = state.jobs[job.id] ?? 0;
    if (assigned <= 0 || !job.inputs) continue;

    let efficiency = 1;
    for (const [resourceId, perWorker] of Object.entries(job.inputs)) {
      const demandPerSecond = (perWorker ?? 0) * assigned;
      if (demandPerSecond <= 0) continue;
      const available =
        (state.resources[resourceId] ?? 0) / step + (production[resourceId] ?? 0);
      efficiency = Math.min(efficiency, available / demandPerSecond);
    }
    efficiency = Math.max(0, Math.min(1, efficiency));
    jobEfficiency[job.id] = efficiency;

    if (efficiency <= 0) continue;

    for (const [resourceId, perWorker] of Object.entries(job.inputs)) {
      consumption[resourceId] =
        (consumption[resourceId] ?? 0) + (perWorker ?? 0) * assigned * efficiency;
    }

    const jobBoost = jobMultiplier(modifiers, job.id);
    for (const [resourceId, perWorker] of Object.entries(job.outputs)) {
      const rate =
        (perWorker ?? 0) *
        assigned *
        efficiency *
        jobBoost *
        resourceMultiplier(state, modifiers, resourceId);
      production[resourceId] = (production[resourceId] ?? 0) + rate;
    }
  }

  // Flat production from buildings and techs, independent of workers.
  for (const [resourceId, amount] of Object.entries(modifiers.flatProduction)) {
    if (!amount) continue;
    production[resourceId] =
      (production[resourceId] ?? 0) + amount * resourceMultiplier(state, modifiers, resourceId);
  }

  // Feeding the tribe. Food is eaten in priority order until demand is met.
  const nutritionDemand =
    state.population * BALANCE.foodPerHeadPerSecond * modifiers.consumption;
  let remainingDemand = nutritionDemand;
  let nutritionSupplied = 0;

  for (const foodResource of FOOD_RESOURCES) {
    if (remainingDemand <= 0) break;
    const nutritionPerUnit = (foodResource.nutrition ?? 0) * modifiers.nutrition;
    if (nutritionPerUnit <= 0) continue;

    const availablePerSecond =
      (state.resources[foodResource.id] ?? 0) / step +
      (production[foodResource.id] ?? 0) -
      (consumption[foodResource.id] ?? 0);
    if (availablePerSecond <= 0) continue;

    const unitsWanted = remainingDemand / nutritionPerUnit;
    const unitsEaten = Math.min(unitsWanted, availablePerSecond);

    consumption[foodResource.id] = (consumption[foodResource.id] ?? 0) + unitsEaten;
    const supplied = unitsEaten * nutritionPerUnit;
    nutritionSupplied += supplied;
    remainingDemand -= supplied;
  }

  const net = zeroed();
  for (const resourceId of RESOURCE_IDS) {
    net[resourceId] = (production[resourceId] ?? 0) - (consumption[resourceId] ?? 0);
  }

  let assignedTotal = 0;
  for (const job of JOBS) assignedTotal += state.jobs[job.id] ?? 0;

  return {
    production,
    consumption,
    net,
    jobEfficiency,
    caps,
    nutritionDemand,
    nutritionSupplied,
    shelter: modifiers.shelter,
    // Only whole members can hold a job, so idleness is whole too.
    idlePopulation: Math.max(0, Math.floor(state.population) - assignedTotal),
  };
}

/** True when the tribe cannot feed itself at the current rates. */
export function isStarving(result: ProductionResult): boolean {
  return result.nutritionSupplied < result.nutritionDemand - 1e-9;
}
